import { useCallback, useMemo } from "react";
import { NUM_COLUMNS_CONTROLS } from "src/api/featureFlags";
import GatheringUserVariant from "src/api/graphql/__generated__/enums/GatheringUserVariant";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { useTrunkLeafNavigationRouter } from "src/components/TrunkLeafNavigation";
import { GatheringSortMode } from "src/contexts/GatheringContext/types";
import { IconComponent } from "src/icons";
import IconLivePhoto from "src/icons/IconLivePhoto";
import IconPhoto from "src/icons/IconPhoto";
import IconVideo from "src/icons/IconVideo";
import {
	GatheringFilterSortCategory,
	GatheringFilterSortNavigationProps,
	GatheringFilterSortSectionOption,
} from "../../types";
import useGatheringFilterSortRefreshListener from "../useGatheringFilterSortRefreshListener";
import useGatheringFilterSortStore from "../useGatheringFilterSortStore";
import useGatheringUsersForFilter from "../useGatheringUsersForFilter";
import useGatheringLabelsOptions from "./hooks/useGatheringLabelsOptions";

const SORT_OPTIONS: { id: GatheringSortMode; text: string }[] = [
	{
		id: "UploadedTime",
		text: "Upload time",
	},
	{
		id: "CaptureTime",
		text: "Capture time",
	},
];
const MEDIA_FILTER_OPTIONS: { id: PhotoVariant; text: string; Icon: IconComponent }[] = [
	{ id: PhotoVariant.Image, text: "Photos", Icon: IconPhoto },
	{ id: PhotoVariant.Video, text: "Videos", Icon: IconVideo },
	{ id: PhotoVariant.LivePhoto, text: "Live Photos", Icon: IconLivePhoto },
];
const MAX_USERS_DISPLAYED = 5;
const useGatheringFilterSortOptions = ({
	gatheringId,
}: {
	gatheringId: string;
}): {
	categories: GatheringFilterSortCategory[];
} => {
	const sortMode = useGatheringFilterSortStore((state) => state.sortMode);
	const filterUserIds = useGatheringFilterSortStore((state) => state.filterUserIds);
	const filterUploaderIds = useGatheringFilterSortStore((state) => state.filterUploaderIds);
	const filterPhotoVariant = useGatheringFilterSortStore((state) => state.filterPhotoVariant);
	const selectSort = useGatheringFilterSortStore((state) => state.selectSort);
	const toggleFilterUserId = useGatheringFilterSortStore((state) => state.toggleFilterUserId);
	const toggleFilterUploaderId = useGatheringFilterSortStore(
		(state) => state.toggleFilterUploaderId,
	);
	const toggleFilterPhotoVariant = useGatheringFilterSortStore(
		(state) => state.toggleFilterPhotoVariant,
	);
	const router = useTrunkLeafNavigationRouter<GatheringFilterSortNavigationProps>();

	const { gathering, refresh: usersRefresh } = useGatheringUsersForFilter(gatheringId);
	const { gathering: gatheringWithUploaders, refresh: uploadersRefresh } =
		useGatheringUsersForFilter(gatheringId, {
			variant: GatheringUserVariant.Uploader,
		});
	const users = useMemo(() => gathering?.users?.payload ?? [], [gathering?.users]);
	const uploaders = useMemo(
		() => gatheringWithUploaders?.users?.payload ?? [],
		[gatheringWithUploaders?.users],
	);
	const numParticipants = useMemo(
		() => gathering?.numParticipants ?? undefined,
		[gathering?.numParticipants],
	);
	const numUploaders = useMemo(
		() => gathering?.numUploaders ?? undefined,
		[gathering?.numUploaders],
	);

	const filterUserOptions: GatheringFilterSortSectionOption[] = useMemo(
		() =>
			users
				.slice(0, MAX_USERS_DISPLAYED)
				.map(({ userId, firstName, lastName, profilePicture }) => {
					return {
						id: userId,
						text: `${firstName ?? ""} ${lastName ?? ""}`,
						imageSrc: profilePicture?.url ?? undefined,
						hasFallbackImage: true,
						onPress: () => toggleFilterUserId(userId),
						isSelected: filterUserIds.includes(userId),
					} satisfies GatheringFilterSortSectionOption;
				}),
		[users, toggleFilterUserId, filterUserIds],
	);

	const filterUploaderOptions: GatheringFilterSortSectionOption[] = useMemo(
		() =>
			uploaders
				.slice(0, MAX_USERS_DISPLAYED)
				.map(({ userId, firstName, lastName, profilePicture }) => {
					return {
						id: userId,
						text: `${firstName ?? ""} ${lastName ?? ""}`,
						imageSrc: profilePicture?.url ?? undefined,
						hasFallbackImage: true,
						onPress: () => toggleFilterUploaderId(userId),
						isSelected: filterUploaderIds.includes(userId),
					} satisfies GatheringFilterSortSectionOption;
				}),
		[uploaders, toggleFilterUploaderId, filterUploaderIds],
	);

	const sortOptions: GatheringFilterSortSectionOption[] = useMemo(
		() =>
			SORT_OPTIONS.map((option) => ({
				...option,
				isSelected: sortMode === option.id,
				onPress: () => selectSort(option.id),
			})),
		[sortMode, selectSort],
	);

	const { labelsOptions } = useGatheringLabelsOptions(gatheringId);
	const filterMiscOptions: GatheringFilterSortSectionOption[] = useMemo(() => {
		const options: GatheringFilterSortSectionOption[] = MEDIA_FILTER_OPTIONS.map((option) => {
			return {
				...option,
				isSelected: filterPhotoVariant.includes(option.id),
				onPress: () => toggleFilterPhotoVariant(option.id),
			};
		});
		options.push(...labelsOptions);
		return options;
	}, [filterPhotoVariant, toggleFilterPhotoVariant, labelsOptions]);

	const handleRefresh = useCallback(async () => {
		await Promise.all([usersRefresh(), uploadersRefresh()]);
	}, [usersRefresh, uploadersRefresh]);
	useGatheringFilterSortRefreshListener(handleRefresh);

	return useMemo(() => {
		const categories: GatheringFilterSortCategory[] = [];
		if (NUM_COLUMNS_CONTROLS) {
			categories.push({
				label: "Layout",
				sections: [
					{
						key: "NumColumns",
						variant: "NumColumns",
					},
				],
			});
		}
		categories.push({
			label: "Sort",
			sections: [
				{
					key: "Sort",
					variant: "SingleSelectList",
					options: sortOptions,
				},
			],
		});
		categories.push({
			label: "Filter",
			sections: [
				{
					key: "FilterMisc",
					variant: "MultiSelectPill",
					options: filterMiscOptions,
				},
				{
					key: "FilterPeople",
					variant: "MultiSelectList",
					headerProps: {
						label: "People",
						numSelected: filterUserIds.length,
						numTotal: numParticipants,
						showSeeAll: !!(numParticipants && numParticipants > 5),
						handleSeeAll: () => router?.navigateToLeaf({ variant: "AllPeople" }),
					},
					options: filterUserOptions,
				},
				{
					key: "FilterUploaders",
					variant: "MultiSelectList",
					headerProps: {
						label: "Uploaders",
						numSelected: filterUploaderIds.length,
						numTotal: numUploaders,
						showSeeAll: !!(numUploaders && numUploaders > 5),
						handleSeeAll: () => router?.navigateToLeaf({ variant: "AllUploaders" }),
					},
					options: filterUploaderOptions,
				},
			],
		});
		return {
			categories,
		};
	}, [
		filterUserOptions,
		filterUploaderOptions,
		filterMiscOptions,
		sortOptions,
		filterUserIds.length,
		filterUploaderIds.length,
		router,
		numParticipants,
		numUploaders,
	]);
};

export default useGatheringFilterSortOptions;
